import { HStack, Box, Stack, Text } from '@chakra-ui/core';
import React, { useMemo } from 'react';
import { OptionSelectionCount } from './questionValidationRules.types';
import { getOptionLabel } from './questionValidationRules.utils';

interface OptionMenuItemProps {
  index: number;
  isSelected: boolean;
  selectionCount: OptionSelectionCount;
  isDisabled: boolean;
}

const CONTAINER_STYLE = {
  width: '100%',
  align: 'flex-start',
  px: 4,
  py: 2,
  spacing: 3,
} as const;

const INDICATOR_BASE_STYLE = {
  as: 'span',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '2px solid',
  w: '18px',
  h: '18px',
  flexShrink: 0,
} as const;

const DOT_STYLE = {
  w: '10px',
  h: '10px',
} as const;

export const OptionMenuItem = React.memo<OptionMenuItemProps>(function OptionMenuItem({
  index,
  isSelected,
  selectionCount,
  isDisabled,
}) {
  const label = useMemo(() => getOptionLabel(index), [index]);

  const isRadio = selectionCount === 1;

  const isEffectivelyDisabled = useMemo(() => {
    if (isSelected) return false;
    return isDisabled;
  }, [isSelected, isDisabled]);

  const borderColor = useMemo(() => {
    if (isEffectivelyDisabled) return 'gray.200';
    if (isSelected) return 'blue.500';
    return 'gray.300';
  }, [isEffectivelyDisabled, isSelected]);

  const indicatorBorderRadius = isRadio ? 'full' : 'sm';

  const indicatorStyle = useMemo(
    () => ({
      ...INDICATOR_BASE_STYLE,
      borderRadius: indicatorBorderRadius,
      borderColor,
    }),
    [indicatorBorderRadius, borderColor],
  );

  const dotStyle = useMemo(() => {
    if (!isSelected) return null;

    const backgroundColor = isEffectivelyDisabled ? /* istanbul ignore next */ 'gray.300' : 'blue.500';
    const borderRadius = isRadio ? 'full' : 'xs';

    return {
      ...DOT_STYLE,
      bg: backgroundColor,
      borderRadius,
    };
  }, [isSelected, isEffectivelyDisabled, isRadio]);

  const textColor = isEffectivelyDisabled ? 'gray.400' : 'inherit';
  const containerOpacity = isEffectivelyDisabled ? 0.6 : 1;
  const pointerEvents = isEffectivelyDisabled ? 'none' : 'auto';

  return (
    <HStack {...CONTAINER_STYLE} opacity={containerOpacity} pointerEvents={pointerEvents}>
      <HStack {...indicatorStyle}>{dotStyle && <Box {...dotStyle} />}</HStack>

      <Stack spacing={0} flex={1}>
        <Text fontSize="sm" fontWeight="bold" lineHeight={1} color={textColor}>
          {label}
        </Text>
      </Stack>
    </HStack>
  );
});
